import { useState, useMemo, useEffect, useRef, type Ref } from 'react'
import {
  Plus, Search, X, CheckCheck, ChevronDown, ChevronLeft, ChevronUp,
  MessageSquare, BookOpen, MoreHorizontal, Pencil, Trash2,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { BucketForm } from './BucketForm'
import { DeleteBucketDialog } from './DeleteBucketDialog'
import { EmptyAreasState } from './EmptyAreasState'
import { AllocationBar } from './BucketHeader'
import { DetailContent } from '@/components/priorities/PriorityDetail'
import { PriorityStatusBadge } from '@/components/priorities/PriorityStatusBadge'
import { PriorityList } from '@/components/priorities/PriorityList'
import { NoteThread } from '@/components/notes/NoteThread'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { useBuckets } from '@/hooks/useBuckets'
import { useAllActivePriorities, usePriority, useMarkPriorityReviewed } from '@/hooks/usePriorities'
import { useAllocation } from '@/hooks/useAllocation'
import { useLastWeeklyReview } from '@/hooks/useLastWeeklyReview'
import { usePriorityInsights } from '@/hooks/usePriorityInsights'
import { useNotesByBucket } from '@/hooks/useNotes'
import { useEntriesForBucket } from '@/hooks/useJournal'
import { getBucketIcon } from '@/lib/bucketIcons'
import { todayISO } from '@/lib/dates'
import {
  WORKING_STATUSES,
  countWorkingPrioritiesByBucket,
  orderAreasByActivity,
  overduePriorities as findOverduePriorities,
} from '@/lib/priorities'
import { cn } from '@/lib/utils'
import type { Bucket, Priority } from '@/types'
import type { AllocationResult, BucketAlloc } from '@/lib/allocation'

// ─── Left panel compact summary ───────────────────────────────────────────────

function LeftPanelSummary({
  allocation,
  priorities,
  overdueCount,
  lastReviewedAt,
  onShowOverdue,
}: {
  allocation: AllocationResult | undefined
  priorities: Priority[] | undefined
  overdueCount: number
  lastReviewedAt: string | null | undefined
  onShowOverdue: () => void
}) {
  const { activeCount, alignmentScore, bandwidthStatus, committedHoursPerWeek } = useMemo(() => {
    const active = (priorities ?? []).filter((p) => WORKING_STATUSES.has(p.status))
    return {
      activeCount: active.length,
      alignmentScore: allocation?.alignmentScore ?? null,
      bandwidthStatus: allocation?.bandwidthStatus ?? 'ok',
      committedHoursPerWeek: allocation?.committedHoursPerWeek ?? 0,
    }
  }, [priorities, allocation])

  return (
    <div className="px-3 py-2 border-b border-border/30 flex-shrink-0 space-y-1">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-muted-foreground">
          <span className="font-semibold text-foreground">{activeCount}</span> active
        </span>
        {committedHoursPerWeek > 0 && (
          <span className={cn(
            'font-medium tabular-nums',
            bandwidthStatus === 'red'   ? 'text-red-500 dark:text-red-400'
            : bandwidthStatus === 'amber' ? 'text-amber-500 dark:text-amber-400'
            : 'text-muted-foreground',
          )}>
            ~{Math.round(committedHoursPerWeek)}h/wk
          </span>
        )}
        {alignmentScore != null && (
          <span className={cn(
            'font-semibold tabular-nums',
            alignmentScore >= 80 ? 'text-emerald-600 dark:text-emerald-400'
            : alignmentScore >= 60 ? 'text-amber-500 dark:text-amber-400'
            : 'text-red-500 dark:text-red-400',
          )}>
            {alignmentScore}% aligned
          </span>
        )}
      </div>
      {overdueCount > 0 && (
        <button
          type="button"
          onClick={onShowOverdue}
          className="text-[11px] text-amber-600 dark:text-amber-400 hover:underline underline-offset-2 transition-colors"
        >
          {overdueCount} {overdueCount === 1 ? 'review' : 'reviews'} due
        </button>
      )}
      {lastReviewedAt !== undefined && lastReviewedAt !== null && (() => {
        const days = Math.floor((Date.now() - new Date(lastReviewedAt).getTime()) / 86_400_000)
        const color =
          days <= 7  ? 'text-emerald-600 dark:text-emerald-400'
          : days <= 14 ? 'text-amber-500 dark:text-amber-400'
          : 'text-red-400'
        return (
          <p className={cn('text-[11px]', color)}>
            Reviewed {days === 0 ? 'today' : days === 1 ? '1d ago' : `${days}d ago`}
          </p>
        )
      })()}
    </div>
  )
}

// ─── Alignment gap panel (compact, left-panel edition) ────────────────────────

function AlignmentGapPanel({
  buckets,
  allocByBucket,
}: {
  buckets: Bucket[]
  allocByBucket: Map<string, BucketAlloc>
}) {
  const [expanded, setExpanded] = useState(false)

  const rows = useMemo(() => {
    return buckets
      .filter((b) => !b.isSystem && !b.archivedAt)
      .map((b, idx) => ({ bucket: b, rank: idx + 1, alloc: allocByBucket.get(b.id) }))
      .filter((r) => r.alloc?.targetAlloc != null)
      .sort((a, b) => Math.abs(b.alloc!.gap ?? 0) - Math.abs(a.alloc!.gap ?? 0))
  }, [buckets, allocByBucket])

  if (rows.length === 0) return null

  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center justify-between w-full px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <span className="text-[10px] uppercase tracking-wider font-semibold">Alignment gaps</span>
        <ChevronDown size={11} className={cn('transition-transform duration-150', expanded && 'rotate-180')} />
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {rows.map(({ bucket, alloc }) => {
            const targetPct = Math.round(alloc!.targetAlloc! * 100)
            const actualPct = Math.round(alloc!.share * 100)
            const gap       = alloc!.gap ?? 0
            const gapPct    = Math.round(Math.abs(gap) * 100)
            const under     = gap > 0.005
            const over      = gap < -0.005
            return (
              <div key={bucket.id}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: bucket.color }} />
                  <span className="flex-1 truncate text-muted-foreground">{bucket.name}</span>
                  <span className={cn(
                    'tabular-nums font-semibold text-[10px]',
                    under ? gapPct >= 10 ? 'text-red-500' : 'text-amber-500'
                    : over ? 'text-emerald-600'
                    : 'text-muted-foreground',
                  )}>
                    {under ? `−${gapPct}%` : over ? `+${gapPct}%` : '✓'}
                  </span>
                </div>
                <div className="relative h-1 bg-muted rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(actualPct, 100)}%`, backgroundColor: bucket.color, opacity: 0.65 }} />
                  <div className="absolute inset-y-0 w-[2px] bg-foreground/20" style={{ left: `${Math.min(targetPct, 100)}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Bucket nav item ──────────────────────────────────────────────────────────

function BucketNavItem({
  bucket,
  isSelected,
  priorityCount,
  alloc,
  onClick,
  large = false,
}: {
  bucket: Bucket
  isSelected: boolean
  priorityCount: number
  alloc?: BucketAlloc
  onClick: () => void
  /** Roomier rows for touch (used inside the mobile bottom sheet). */
  large?: boolean
}) {
  const BucketIconEl = getBucketIcon(bucket.icon)
  const gap = alloc?.gap ?? null
  const underInvested = gap !== null && gap > 0.05
  const overInvested  = gap !== null && gap < -0.05

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative w-full flex items-center text-left transition-all duration-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        large ? 'gap-3 px-5 py-3.5' : 'gap-2 px-3 py-2.5',
        isSelected
          ? 'text-foreground'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
      )}
      aria-pressed={isSelected}
    >
      {/* Left accent bar */}
      <div
        className={cn('absolute left-0 inset-y-1.5 w-[3px] rounded-r transition-all duration-150', isSelected ? 'opacity-100' : 'opacity-0')}
        style={{ backgroundColor: bucket.color }}
      />
      {/* Selected tint */}
      {isSelected && (
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none rounded" style={{ backgroundColor: bucket.color }} />
      )}

      {/* Icon or color dot */}
      {BucketIconEl ? (
        <BucketIconEl size={large ? 16 : 13} style={{ color: bucket.color }} className="flex-shrink-0" />
      ) : (
        <span className={cn('rounded-full flex-shrink-0 opacity-80', large ? 'w-3 h-3' : 'w-2.5 h-2.5')} style={{ backgroundColor: bucket.color }} />
      )}

      {/* Name + count */}
      <div className="flex-1 min-w-0">
        <p className={cn('leading-tight truncate', large ? 'text-base' : 'text-sm', isSelected && 'font-medium')}>{bucket.name}</p>
        <p className={cn('text-muted-foreground/60 mt-0.5', large ? 'text-xs' : 'text-[10px]')}>
          {priorityCount} {priorityCount === 1 ? 'priority' : 'priorities'}
        </p>
      </div>

      {/* Gap indicator dot */}
      {underInvested && (
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" title="Under-invested vs. target" />
      )}
      {overInvested && (
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" title="Over-invested vs. target" />
      )}
    </button>
  )
}

// ─── Search results ───────────────────────────────────────────────────────────

function SearchResults({
  results,
  query,
  bucketMap,
  onSelect,
  showMarkReviewed = false,
}: {
  results: Priority[]
  query: string
  bucketMap: Map<string, Bucket>
  onSelect: (id: string) => void
  showMarkReviewed?: boolean
}) {
  const markPriorityReviewed = useMarkPriorityReviewed()

  if (results.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic py-8 text-center">
        {showMarkReviewed ? 'All reviews are up to date.' : `No priorities match "${query}".`}
      </p>
    )
  }

  return (
    <ul className="space-y-0.5">
      {results.map((p) => {
        const bucket = bucketMap.get(p.bucketId ?? '')
        const daysOverdue = p.nextReviewDate
          ? Math.floor((Date.now() - new Date(p.nextReviewDate).getTime()) / 86_400_000)
          : null
        return (
          <li key={p.id} className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onSelect(p.id)}
              className="flex-1 flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted/60 text-left group transition-colors min-w-0"
            >
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: bucket?.color ?? '#888' }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{p.title}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {bucket?.name ?? ''}
                  {showMarkReviewed && daysOverdue !== null && (
                    <span className="ml-1 text-amber-600 dark:text-amber-400">· {daysOverdue}d overdue</span>
                  )}
                </p>
              </div>
              {!showMarkReviewed && (
                <span className="flex-shrink-0 max-md:opacity-100 opacity-0 group-hover:opacity-100 transition-opacity">
                  <PriorityStatusBadge status={p.status} />
                </span>
              )}
            </button>
            {showMarkReviewed && (
              <button
                type="button"
                onClick={() => markPriorityReviewed.mutate(p.id)}
                title={`Mark reviewed — advance next review by ${p.cadence ?? '4 weeks'}`}
                className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 rounded text-xs text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors border border-emerald-200 dark:border-emerald-800"
              >
                <CheckCheck size={12} />
                Done
              </button>
            )}
          </li>
        )
      })}
    </ul>
  )
}

// ─── Bucket detail panel (right panel — bucket view) ─────────────────────────

function BucketDetailPanel({
  bucket,
  alloc,
  onSelectPriority,
  insights,
}: {
  bucket: Bucket
  alloc?: BucketAlloc
  onSelectPriority: (id: string) => void
  insights?: Record<string, string>
}) {
  const BucketIconEl = getBucketIcon(bucket.icon)
  const notes        = useNotesByBucket(bucket.id)
  const linkedEntries = useEntriesForBucket(bucket.id)
  const [notesOpen, setNotesOpen]     = useState(false)
  const [entriesOpen, setEntriesOpen] = useState(false)
  const [showEditForm, setShowEditForm]       = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const noteCount   = notes?.length ?? 0
  const entryCount  = linkedEntries?.length ?? 0
  const isMisaligned = (alloc?.gap ?? 0) > 0.05

  return (
    <div className="animate-panel-in">
      {/* ── Header bar — flat 15% tint of the area color ── */}
      <div
        className="h-[54px] flex items-center gap-2.5 px-4 sm:px-6 md:px-8 border-b border-border/30"
        style={{ backgroundColor: `${bucket.color}26` }}
      >
        {BucketIconEl && (
          <BucketIconEl size={18} style={{ color: bucket.color }} className="flex-shrink-0" aria-hidden />
        )}
        <h2 className="text-lg font-semibold leading-tight tracking-tight truncate">{bucket.name}</h2>
        {bucket.intention && (
          <p className="hidden lg:block flex-1 min-w-0 text-xs text-muted-foreground italic truncate">
            "{bucket.intention}"
          </p>
        )}

        <div className="ml-auto flex items-center flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 md:h-8 md:w-8 text-muted-foreground" aria-label="Area actions">
                <MoreHorizontal size={15} />
              </Button>
            </DropdownMenuTrigger>
            {/* Target % and color are edited in the Edit form. */}
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuItem onClick={() => setShowEditForm(true)}>
                <Pencil size={13} className="mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowDeleteDialog(true)} className="text-destructive focus:text-destructive">
                <Trash2 size={13} className="mr-2" />
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ── Intention (small screens) + allocation bar ── */}
      {(bucket.intention || (alloc && (alloc.share > 0 || alloc.targetAlloc != null))) && (
        <div className="px-4 sm:px-6 md:px-8 pt-4 space-y-1.5">
          {bucket.intention && (
            <p className="lg:hidden text-sm text-muted-foreground italic leading-relaxed">"{bucket.intention}"</p>
          )}
          {alloc && (alloc.share > 0 || alloc.targetAlloc != null) && (
            <div className="max-w-sm">
              <AllocationBar alloc={alloc} color={bucket.color} />
            </div>
          )}
        </div>
      )}

      {/* ── Priorities section ── */}
      <div className="px-4 sm:px-6 md:px-8 py-6">
        <PriorityList
          bucketId={bucket.id}
          bucketColor={bucket.color}
          bucketIcon={bucket.icon}
          onSelectPriority={onSelectPriority}
          insights={insights}
          bucketMisaligned={isMisaligned}
        />
      </div>

      {/* ── Notes drawer — visually recessed to read as a separate surface ── */}
      <div className={cn(
        'px-4 sm:px-6 md:px-8 py-4 border-t border-border/30 transition-colors',
        notesOpen && 'bg-muted/40 dark:bg-muted/15 border-t-border/60',
      )}>
        <Button
          type="button"
          variant="outline"
          onClick={() => setNotesOpen((o) => !o)}
          className="h-11 md:h-9 gap-1.5 px-3.5"
          aria-expanded={notesOpen}
        >
          <MessageSquare />
          {notesOpen ? 'Hide notes' : noteCount > 0 ? `${noteCount} ${noteCount === 1 ? 'note' : 'notes'}` : 'Add area note'}
          <ChevronDown className={cn('transition-transform duration-150', notesOpen && 'rotate-180')} />
        </Button>
        {notesOpen && (
          <div className="mt-3 animate-panel-in rounded-lg border border-border/50 bg-background/70 dark:bg-background/40 shadow-inner p-3 sm:p-4">
            <NoteThread
              notes={notes}
              entity={{ bucketId: bucket.id }}
              placeholder="Add a note about this life area…"
            />
          </div>
        )}
      </div>

      {/* ── Linked journal entries ── */}
      {entryCount > 0 && (
        <div className="px-4 sm:px-6 md:px-8 py-4 border-t border-border/30">
          <button
            type="button"
            onClick={() => setEntriesOpen((o) => !o)}
            className={cn(
              'flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-0.5',
              entriesOpen && 'text-foreground',
            )}
            aria-expanded={entriesOpen}
          >
            <BookOpen size={12} />
            {entriesOpen ? 'Hide journal entries' : `${entryCount} journal ${entryCount === 1 ? 'entry' : 'entries'}`}
          </button>
          {entriesOpen && (
            <div className="mt-3 space-y-3">
              {linkedEntries!.map((e) => (
                <div key={e.id} className="rounded-lg border border-border/50 bg-card shadow-sm overflow-hidden">
                  <div
                    className="h-[2px]"
                    style={{ backgroundColor: bucket.color, opacity: 0.5 }}
                  />
                  <div className="px-3 py-2.5">
                    <p className="text-[10px] text-muted-foreground/60 mb-1 uppercase tracking-wide">
                      {format(new Date(e.entryDate), 'MMM d, yyyy')} · {e.type === 'weekly_review' ? 'Review' : e.type === 'discernment' ? 'Discernment' : 'Free write'}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                      {e.body.trim() || <em>Empty entry</em>}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="pb-[calc(7rem+var(--sab))] md:pb-16" />

      <BucketForm bucket={bucket} open={showEditForm} onOpenChange={setShowEditForm} />
      <DeleteBucketDialog bucket={showDeleteDialog ? bucket : null} onOpenChange={(open) => { if (!open) setShowDeleteDialog(false) }} />
    </div>
  )
}

// ─── Inline priority detail (right panel — priority view) ────────────────────

function InlinePriorityDetail({
  priorityId,
  bucket,
  onBack,
}: {
  priorityId: string
  bucket: Bucket | null
  onBack: () => void
}) {
  const priority = usePriority(priorityId)

  return (
    <div className="h-full flex flex-col animate-slide-in-right">
      {/* Back nav */}
      <div
        className="flex items-center gap-2 px-4 md:px-8 pt-4 md:pt-5 pb-3 flex-shrink-0 border-b border-border/30"
        style={bucket ? { borderBottomColor: `${bucket.color}33` } : undefined}
      >
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group py-1.5 -my-1.5 pr-2"
        >
          <ChevronLeft size={15} className="transition-transform group-hover:-translate-x-0.5" />
          {bucket ? bucket.name : 'Back'}
        </button>
      </div>

      {/* Priority detail content */}
      <div className="flex flex-col flex-1 min-h-0 px-4 md:px-6 pt-6 pb-[calc(5rem+var(--sab))] md:pb-0">
        {priority
          ? <DetailContent priority={priority} onClose={onBack} />
          : <p className="text-sm text-muted-foreground mt-8 text-center">Loading…</p>}
      </div>
    </div>
  )
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function BucketListSkeleton() {
  return (
    <div className="flex -mx-4 md:-mx-6 -mt-6 md:-mt-8 -mb-6 md:-mb-8 h-below-header overflow-hidden animate-pulse">
      <div className="hidden md:flex w-[220px] border-r border-border/40 bg-muted/10 flex-col gap-2 p-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded-md" />
        ))}
      </div>
      <div className="flex-1 p-8 space-y-4">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="h-4 w-64 bg-muted/60 rounded" />
        <div className="h-1.5 w-40 bg-muted/40 rounded" />
        <div className="mt-8 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 bg-muted rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Mobile: collapsed life-area bar (bottom of screen) ───────────────────────
// Styled as the closed state of a bottom sheet — grabber, raised top corners,
// upward shadow — so it reads as "drag me up". Tap or swipe up to open.

function MobileAreaBar({
  bucket,
  priorityCount,
  onOpen,
  onAddArea,
  buttonRef,
}: {
  bucket: Bucket | null
  priorityCount: number
  onOpen: () => void
  /** Set when there are no areas yet: the bar becomes the "new life area" button. */
  onAddArea?: () => void
  buttonRef?: Ref<HTMLButtonElement>
}) {
  const BucketIconEl = bucket ? getBucketIcon(bucket.icon) : null

  if (onAddArea) {
    return (
      <button
        ref={buttonRef}
        type="button"
        onClick={onAddArea}
        className="md:hidden fixed inset-x-0 bottom-0 z-40 rounded-t-2xl border-t border-border bg-card shadow-[0_-6px_24px_rgba(0,0,0,0.14)] pb-[var(--sab)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center justify-center gap-2 px-5 py-4 text-sm font-medium">
          <Plus size={18} aria-hidden />
          New life area
        </span>
      </button>
    )
  }

  return (
    <motion.button
      ref={buttonRef}
      type="button"
      onClick={onOpen}
      onPanEnd={(_, info) => { if (info.offset.y < -24) onOpen() }}
      className="md:hidden fixed inset-x-0 bottom-0 z-40 rounded-t-2xl border-t border-border bg-card shadow-[0_-6px_24px_rgba(0,0,0,0.14)] pb-[var(--sab)] text-left touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={bucket ? `Life area: ${bucket.name}. Open life area picker` : 'Open life area picker'}
    >
      <div className="flex justify-center pt-2 pb-1" aria-hidden>
        <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
      </div>
      <div className="flex items-center gap-3 px-5 pb-3.5">
        {bucket ? (
          <>
            {BucketIconEl ? (
              <BucketIconEl size={18} style={{ color: bucket.color }} className="flex-shrink-0" aria-hidden />
            ) : (
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: bucket.color }} aria-hidden />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-tight truncate">{bucket.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {priorityCount} {priorityCount === 1 ? 'priority' : 'priorities'}
              </p>
            </div>
          </>
        ) : (
          <p className="flex-1 text-sm text-muted-foreground">Choose a life area</p>
        )}
        <ChevronUp size={18} className="flex-shrink-0 text-muted-foreground" aria-hidden />
      </div>
    </motion.button>
  )
}

// ─── BucketList ───────────────────────────────────────────────────────────────

export function BucketList() {
  const allBuckets      = useBuckets()
  const allPriorities   = useAllActivePriorities()
  const allocation      = useAllocation(30)
  const lastWeeklyReview = useLastWeeklyReview()
  const insights        = usePriorityInsights()

  const [selectedBucketId,   setSelectedBucketId]   = useState<string | null>(null)
  const [selectedPriorityId, setSelectedPriorityId] = useState<string | null>(null)
  const [showNewForm,        setShowNewForm]         = useState(false)
  const [areaSheetOpen,      setAreaSheetOpen]       = useState(false)
  const [sheetSearch,        setSheetSearch]         = useState('')
  const [search,             setSearch]             = useState('')
  const [filterOverdue,      setFilterOverdue]      = useState(false)

  // The "new life area" buttons the empty state's arrow points at (desktop panel, phone bar).
  const addAreaButtonRef = useRef<HTMLButtonElement>(null)
  const mobileAddAreaRef = useRef<HTMLButtonElement>(null)

  const activePriorityCountByBucket = useMemo(
    () => countWorkingPrioritiesByBucket(allPriorities ?? []),
    [allPriorities],
  )

  // Auto-ordered: most active priorities first, alphabetical on ties (incl. all-zero)
  const userBuckets = useMemo(
    () => orderAreasByActivity(allBuckets ?? [], activePriorityCountByBucket),
    [allBuckets, activePriorityCountByBucket],
  )

  // Auto-select first bucket on load
  useEffect(() => {
    if (!selectedBucketId && userBuckets.length > 0) {
      setSelectedBucketId(userBuckets[0]!.id)
    }
  }, [userBuckets, selectedBucketId])

  // Global event to open a priority detail
  useEffect(() => {
    function handler(e: Event) {
      const id = (e as CustomEvent<{ id: string }>).detail.id
      setSelectedPriorityId(id)
    }
    window.addEventListener('plumb:open-priority', handler)
    return () => window.removeEventListener('plumb:open-priority', handler)
  }, [])

  const allocByBucket = useMemo<Map<string, BucketAlloc>>(() => {
    if (!allocation) return new Map()
    return new Map(allocation.byBucket.map((b) => [b.bucketId, b]))
  }, [allocation])

  const bucketMap = useMemo<Map<string, Bucket>>(
    () => new Map((allBuckets ?? []).map((b) => [b.id, b])),
    [allBuckets],
  )

  const overduePriorities = useMemo<Priority[]>(
    () => findOverduePriorities(allPriorities ?? [], todayISO()),
    [allPriorities],
  )
  const overdueCount = overduePriorities.length

  const searchResults = useMemo<Priority[] | null>(() => {
    const q = search.trim().toLowerCase()
    if (filterOverdue && !q) return overduePriorities
    if (!q || !allPriorities) return null
    return allPriorities.filter((p) => p.title.toLowerCase().includes(q))
  }, [search, allPriorities, filterOverdue, overduePriorities])

  const selectedBucket = useMemo(
    () => userBuckets.find((b) => b.id === selectedBucketId) ?? null,
    [userBuckets, selectedBucketId],
  )

  if (allBuckets === undefined) return <BucketListSkeleton />

  const showSearch           = !!(search || filterOverdue)
  const isShowingPriorityDetail = !!selectedPriorityId && !showSearch

  return (
    <div className="flex -mx-4 md:-mx-6 -mt-6 md:-mt-8 -mb-6 md:-mb-8 h-below-header overflow-hidden">

      {/* ── Left nav panel (desktop only) ── */}
      <aside className="hidden md:flex w-[220px] flex-shrink-0 border-r border-border/50 flex-col bg-muted/10 dark:bg-muted/5 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 pt-4 pb-2.5 flex-shrink-0">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Life Areas
          </span>
          <button
            ref={addAreaButtonRef}
            type="button"
            onClick={() => setShowNewForm(true)}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Add new life area"
          >
            <Plus size={14} />
          </button>
        </div>

        {/* Compact stats */}
        <LeftPanelSummary
          allocation={allocation}
          priorities={allPriorities}
          overdueCount={overdueCount}
          lastReviewedAt={lastWeeklyReview === undefined ? undefined : lastWeeklyReview?.createdAt ?? null}
          onShowOverdue={() => { setSearch(''); setFilterOverdue(true) }}
        />

        {/* Search */}
        <div className="px-3 py-2 border-b border-border/30 flex-shrink-0">
          <div className="relative">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); if (filterOverdue) setFilterOverdue(false) }}
              placeholder={filterOverdue ? 'Filter overdue…' : 'Search priorities…'}
              className="w-full h-7 pl-6 pr-6 text-xs bg-muted/50 border border-border/50 rounded-md focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground/50 transition-colors"
            />
            {(search || filterOverdue) && (
              <button
                type="button"
                onClick={() => { setSearch(''); setFilterOverdue(false) }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear filter"
              >
                <X size={11} />
              </button>
            )}
          </div>
        </div>

        {/* Bucket nav list */}
        <div className="flex-1 overflow-y-auto py-1">
          {userBuckets.map((bucket) => (
            <BucketNavItem
              key={bucket.id}
              bucket={bucket}
              isSelected={selectedBucketId === bucket.id && !showSearch}
              priorityCount={activePriorityCountByBucket.get(bucket.id) ?? 0}
              alloc={allocByBucket.get(bucket.id)}
              onClick={() => {
                setSelectedBucketId(bucket.id)
                setSelectedPriorityId(null)
                setSearch('')
                setFilterOverdue(false)
              }}
            />
          ))}
          {userBuckets.length === 0 && (
            <p className="text-xs text-muted-foreground px-3 py-6 text-center leading-relaxed">
              No areas yet.<br />Add one to get started.
            </p>
          )}
        </div>

        {/* Alignment gaps — collapsible at bottom */}
        <div className="flex-shrink-0 border-t border-border/30">
          <AlignmentGapPanel
            buckets={allBuckets}
            allocByBucket={allocByBucket}
          />
        </div>
      </aside>

      {/* ── Right content panel ── */}
      <div
        className={cn(
          'flex-1 min-w-0',
          isShowingPriorityDetail ? 'overflow-hidden' : 'overflow-y-auto',
        )}
      >
        {showSearch ? (
          <div className="px-4 md:px-8 py-6">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
              {filterOverdue && !search ? 'Overdue reviews' : `Results for "${search}"`}
            </h2>
            {filterOverdue && !search && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
                {overduePriorities.length} {overduePriorities.length === 1 ? 'priority' : 'priorities'} with overdue reviews — sorted by most overdue
              </p>
            )}
            <SearchResults
              results={searchResults!}
              query={filterOverdue && !search ? '' : search.trim()}
              bucketMap={bucketMap}
              onSelect={(id) => { setSelectedPriorityId(id); setSearch(''); setFilterOverdue(false) }}
              showMarkReviewed={filterOverdue && !search}
            />
          </div>
        ) : isShowingPriorityDetail ? (
          <InlinePriorityDetail
            priorityId={selectedPriorityId!}
            bucket={selectedBucket}
            onBack={() => setSelectedPriorityId(null)}
          />
        ) : selectedBucket ? (
          <BucketDetailPanel
            key={selectedBucket.id}
            bucket={selectedBucket}
            alloc={allocByBucket.get(selectedBucket.id)}
            onSelectPriority={(id) => setSelectedPriorityId(id)}
            insights={insights}
          />
        ) : userBuckets.length === 0 ? (
          <EmptyAreasState targetRefs={[addAreaButtonRef, mobileAddAreaRef]} />
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            Select a life area to view its priorities.
          </div>
        )}
      </div>

      {/* ── Mobile: current life area bar + picker sheet ── */}
      <MobileAreaBar
        bucket={selectedBucket}
        priorityCount={selectedBucket ? (activePriorityCountByBucket.get(selectedBucket.id) ?? 0) : 0}
        onOpen={() => setAreaSheetOpen(true)}
        onAddArea={userBuckets.length === 0 ? () => setShowNewForm(true) : undefined}
        buttonRef={mobileAddAreaRef}
      />
      <BottomSheet
        open={areaSheetOpen}
        onOpenChange={(open) => { setAreaSheetOpen(open); if (!open) setSheetSearch('') }}
        title="Life Areas"
        className="max-h-[50dvh]"
        titleAccessory={
          <button
            type="button"
            onClick={() => { setAreaSheetOpen(false); setShowNewForm(true) }}
            className="p-2.5 -m-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Add new life area"
          >
            <Plus size={18} />
          </button>
        }
      >
        <LeftPanelSummary
          allocation={allocation}
          priorities={allPriorities}
          overdueCount={overdueCount}
          lastReviewedAt={lastWeeklyReview === undefined ? undefined : lastWeeklyReview?.createdAt ?? null}
          onShowOverdue={() => {
            setAreaSheetOpen(false)
            setSearch('')
            setFilterOverdue(true)
          }}
        />
        {/* Search — results render inside the sheet */}
        <div className="px-5 pb-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              value={sheetSearch}
              onChange={(e) => setSheetSearch(e.target.value)}
              placeholder="Search priorities…"
              className="w-full h-11 pl-9 pr-9 text-sm bg-muted/50 border border-border/50 rounded-lg focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground/50"
            />
            {sheetSearch && (
              <button
                type="button"
                onClick={() => setSheetSearch('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {sheetSearch.trim() ? (
          <div className="px-3 pb-4">
            <SearchResults
              results={(allPriorities ?? []).filter((p) =>
                p.title.toLowerCase().includes(sheetSearch.trim().toLowerCase()),
              )}
              query={sheetSearch.trim()}
              bucketMap={bucketMap}
              onSelect={(id) => {
                setSelectedPriorityId(id)
                setSearch('')
                setFilterOverdue(false)
                setSheetSearch('')
                setAreaSheetOpen(false)
              }}
            />
          </div>
        ) : (
          <div className="py-1 pb-4">
            {userBuckets.map((bucket) => (
              <BucketNavItem
                key={bucket.id}
                bucket={bucket}
                large
                isSelected={selectedBucketId === bucket.id && !showSearch}
                priorityCount={activePriorityCountByBucket.get(bucket.id) ?? 0}
                alloc={allocByBucket.get(bucket.id)}
                onClick={() => {
                  setSelectedBucketId(bucket.id)
                  setSelectedPriorityId(null)
                  setSearch('')
                  setFilterOverdue(false)
                  setAreaSheetOpen(false)
                }}
              />
            ))}
            {userBuckets.length === 0 && (
              <p className="text-sm text-muted-foreground px-5 py-8 text-center leading-relaxed">
                No areas yet. Add one to get started.
              </p>
            )}
          </div>
        )}
      </BottomSheet>

      <BucketForm open={showNewForm} onOpenChange={setShowNewForm} />
    </div>
  )
}
